import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Card, Typography, Space, Button, message, Row, Col, Modal, Form, Input, Select, DatePicker } from 'antd'
import { 
  ReloadOutlined, 
  CalendarOutlined
} from '@ant-design/icons'
import { RootState } from '../../store'
import { fetchPlanList, setFilters, setPagination } from '../../store/slices/planogramPlansSlice'

import { PlanFilters, SortConfig } from '../../types'
import FilterPanel from '../../components/common/FilterPanel'
import PlanTable from './components/PlanTable'
import BatchActionToolbar from '../../components/common/BatchActionToolbar'
import planService from '../../services/planService'
import { UserAddOutlined } from '@ant-design/icons'

const { Title } = Typography
const { Option } = Select

const PlanogramPlanList: React.FC = () => {
  const { t } = useTranslation()
  const [form] = Form.useForm();
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { plans, loading, error, filters, pagination } = useSelector(
    (state: RootState) => state.planogramPlans
  )
  
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>([])
  const [sortConfig, setSortConfig] = useState<SortConfig>()

  // Load plan list on component mount and when filters/pagination change
  useEffect(() => {
    handleLoadPlans()
  }, [filters, pagination.currentPage, pagination.pageSize])

  const handleLoadPlans = useCallback(() => {
    dispatch(fetchPlanList({ filters, pagination }) as any)
  }, [dispatch, filters, pagination])

  const handleFilterChange = useCallback((values: any) => {
    // 将表单值转换为PlanFilters格式
    const newFilters: PlanFilters = {
      keyword: values.keyword,
      planType: values.planType,
      status: values.status,
      templateOwner: values.templateOwner,
      templateReference: values.templateReference,
      generateTimeRange: values.generateTimeRange ? [
        values.generateTimeRange[0]?.toDate(),
        values.generateTimeRange[1]?.toDate()
      ] : undefined,
      startTimeRange: values.startTimeRange ? [
        values.startTimeRange[0]?.toDate(),
        values.startTimeRange[1]?.toDate()
      ] : undefined,
      lastMilestonePlanTimeRange: values.lastMilestonePlanTimeRange ? [
        values.lastMilestonePlanTimeRange[0]?.toDate(),
        values.lastMilestonePlanTimeRange[1]?.toDate()
      ] : undefined,
    }
    
    dispatch(setFilters(newFilters))
    // 重置到第一页并触发查询
    dispatch(setPagination({ currentPage: 1 }))
  }, [dispatch])

  const handleSearch = useCallback(() => {
    // 重置到第一页并触发查询
    dispatch(setPagination({ currentPage: 1 }))
  }, [dispatch])

  const handleFilterReset = useCallback(() => {
    form.resetFields();
    // 重置筛选条件
    dispatch(setFilters({}))
    // 重置到第一页
    dispatch(setPagination({ currentPage: 1 }))
  }, [dispatch])

  const handlePaginationChange = useCallback((page: number, pageSize?: number) => {
    dispatch(setPagination({ 
      currentPage: page, 
      pageSize: pageSize || pagination.pageSize 
    }))
  }, [dispatch, pagination.pageSize])

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    dispatch(setPagination({ 
      currentPage: 1, // Reset to first page when page size changes
      pageSize: size 
    }))
  }, [dispatch])

  const handleRefresh = useCallback(() => {
    handleLoadPlans()
    message.success(t('planManagement.list.messages.refreshSuccess'))
  }, [handleLoadPlans, t])

  const handleRowSelectionChange = useCallback((selectedRowKeys: React.Key[]) => {
    setSelectedPlanIds(selectedRowKeys as string[])
  }, [])

  const handleBatchAssign = useCallback(() => {
    if (selectedPlanIds.length === 0) {
      message.warning(t('planManagement.list.messages.batchAssignWarning'))
      return
    }
    // TODO: Implement batch assignment logic
    message.info(t('planManagement.list.messages.batchAssignInfo', { count: selectedPlanIds.length }))
  }, [selectedPlanIds, t])

  const handleViewDetail = useCallback((Id: string) => {
    navigate(`/detail/${Id}`)
  }, [navigate])

  const handleAssignPersonnel = useCallback((Id: string) => {
    navigate(`/assignment/${Id}`, { state: { from: '/plans' } })
  }, [navigate])

  const handleActivatePlan = useCallback((Id: string) => {
    navigate(`/activation/${Id}`)
  }, [navigate])

  const handlePausePlan = useCallback(async (Id: string) => {
    Modal.confirm({
      title: t('planManagement.list.messages.pausePlanConfirm'),
      content: (
        <div>
          <p>{t('planManagement.list.messages.pausePlanContent')}</p>
          <p style={{ color: '#faad14', marginBottom: 0 }}>
            {t('planManagement.list.messages.pausePlanWarning')}
          </p>
        </div>
      ),
      okText: t('planManagement.list.messages.pausePlanConfirm'),
      cancelText: t('common.cancel'),
      okType: 'primary',
      onOk: async () => {
        try {
          await planService.pausePlan(Id)
          message.success(t('planManagement.list.messages.pausePlanSuccess'))
          handleLoadPlans() // 刷新列表
        } catch (error) {
          message.error(t('planManagement.list.messages.pausePlanFailed'))
        }
      }
    })
  }, [handleLoadPlans, t])

  const handleCancelPlan = useCallback(async (Id: string) => {
    try {
      const res = await planService.cancelPlan(Id)
      if (!res.success) {
        message.error(res.errorMessage)
        return
      }
      message.success(t('planManagement.list.messages.cancelPlanSuccess'))
      handleLoadPlans() // 刷新列表
    } catch (error) {
      message.error(t('planManagement.list.messages.cancelPlanFailed'))
    }
  }, [handleLoadPlans, t])

  const handleSwitchToCalendar = useCallback(() => {
    navigate('/calendar')
  }, [navigate])

  const handleSortChange = useCallback((newSortConfig: SortConfig) => {
    setSortConfig(newSortConfig)
    // In a real implementation, this would trigger a new API call with sorting
    const direction = newSortConfig.direction === 'asc' ? t('planManagement.list.messages.sortAsc') : t('planManagement.list.messages.sortDesc')
    message.info(t('planManagement.list.messages.sortBy', { field: newSortConfig.field, direction }))
  }, [t])

  // Show error message if there's an error
  useEffect(() => {
    if (error) {
      message.error(error)
    }
  }, [error])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>

     <FilterPanel
      form={form}
      
      onFilter={handleFilterChange}
      onReset={handleFilterReset}
      title={t('menu.plan')}
      className="plan-filter-panel"
      collapsible={true}
      defaultCollapsed={false}
    >
      {/* 基础筛选条件 */}
      <Col xs={24} sm={12} md={8} lg={6}>
        <Form.Item name="keyword" label={t('planManagement.list.filterPanel.keyword')}>
          <Input
            placeholder={t('planManagement.list.filterPanel.keywordPlaceholder')}
            allowClear
            disabled={loading}
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} md={8} lg={6}>
        <Form.Item name="planType" label={t('planManagement.list.filterPanel.planType')}>
          <Select placeholder={t('planManagement.list.filterPanel.planTypePlaceholder')} allowClear disabled={loading}>
            <Option value="Major Plans">{t('planManagement.list.planTypes.Major')}</Option>
            <Option value="Regular Plans">{t('planManagement.list.planTypes.Regular')}</Option>
            <Option value="Minor Plans">{t('planManagement.list.planTypes.Minor')}</Option>
            <Option value="Urgent Plans">{t('planManagement.list.planTypes.Urgent')}</Option>
            <Option value="Special Plans">{t('planManagement.list.planTypes.Special')}</Option>
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} md={8} lg={6}>
        <Form.Item name="status" label={t('planManagement.list.filterPanel.status')}>
          <Select placeholder={t('planManagement.list.filterPanel.statusPlaceholder')} allowClear disabled={loading}>
            <Option value="Pending">{t('planManagement.list.status.Pending')}</Option>
            <Option value="InProgress">{t('planManagement.list.status.InProgress')}</Option>
            <Option value="Completed">{t('planManagement.list.status.Completed')}</Option>
            <Option value="Cancelled">{t('planManagement.list.status.Cancelled')}</Option>
            <Option value="Paused">{t('planManagement.list.status.Paused')}</Option>
          </Select>
        </Form.Item>
      </Col>
      
      {/* 高级筛选条件 */}
      <Col xs={24} sm={12} md={8} lg={6}>
        <Form.Item name="templateOwner" label={t('planManagement.list.filterPanel.templateOwner')}>
          <Input
            placeholder={t('planManagement.list.filterPanel.templateOwnerPlaceholder')}
            allowClear
            disabled={loading}
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} md={8} lg={6}>
        <Form.Item name="templateReference" label={t('planManagement.list.filterPanel.templateReference')}>
          <Input
            placeholder={t('planManagement.list.filterPanel.templateReferencePlaceholder')}
            allowClear
            disabled={loading}
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item name="generateTimeRange" label={t('planManagement.list.filterPanel.generateTimeRange')}>
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            placeholder={[t('planManagement.list.filterPanel.startTimePlaceholder'), t('planManagement.list.filterPanel.endTimePlaceholder')]}
            disabled={loading}
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item name="startTimeRange" label={t('planManagement.list.filterPanel.startTimeRange')}>
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            placeholder={[t('planManagement.list.filterPanel.startTimePlaceholder'), t('planManagement.list.filterPanel.endTimePlaceholder')]}
            disabled={loading}
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item name="lastMilestonePlanTimeRange" label={t('planManagement.list.filterPanel.lastMilestonePlanTime')}>
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            placeholder={[t('planManagement.list.filterPanel.startTimePlaceholder'), t('planManagement.list.filterPanel.endTimePlaceholder')]}
            disabled={loading}
          />
        </Form.Item>
      </Col>
    </FilterPanel>

    {/* Plan List Card */}
    <Card style={{ 
      borderRadius: '16px',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
      border: '1px solid #e2e8f0',
      backgroundColor: '#ffffff'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>   
        <Space size="middle">
          {/* Action Buttons */}
          
          <Button
            type="primary"
            icon={<CalendarOutlined />}
            onClick={handleSwitchToCalendar}
            style={{ 
              borderRadius: '8px',
              backgroundColor: '#3b82f6',
              borderColor: '#3b82f6',
              boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
            }}
          >
            {t('planManagement.list.actions.switchToCalendar')}
          </Button>
        </Space>
      </div>

      <PlanTable
        plans={plans}
        loading={loading}
        pagination={pagination}
        sortConfig={sortConfig}
        onPaginationChange={handlePaginationChange}
        onPageSizeChange={handlePageSizeChange}
        onSortChange={handleSortChange}
        onViewDetail={handleViewDetail}
        onAssignPersonnel={handleAssignPersonnel}
        onActivatePlan={handleActivatePlan}
        onPausePlan={handlePausePlan}
        onCancelPlan={handleCancelPlan}
      />
    </Card>
    </div>
  )
}

export default PlanogramPlanList